// frontend/modules/service-metrics/components/ServiceMetricsTrendChart.tsx
import React from 'react';
import { Card, Empty } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import type { TrendDataPoint } from '../types/service-metrics.types';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    ComposedChart
} from 'recharts';

interface ServiceMetricsTrendChartProps {
    data: TrendDataPoint[];
    loading?: boolean;
    title?: string;
}

const ServiceMetricsTrendChart: React.FC<ServiceMetricsTrendChartProps> = ({ 
    data, 
    loading,
    title = 'Request Trend'
}) => {
    const { dark } = useTheme();

    const formattedData = data.map(point => ({
        ...point,
        date: point.timestamp ? new Date(point.timestamp).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
        }) : '',
        successRate: (point.requests ?? 0) > 0 
            ? (((point.successes ?? 0) / (point.requests ?? 1)) * 100).toFixed(1) 
            : 100
    }));

    if (!data || data.length === 0) {
        return (
            <Card 
                title={title} 
                loading={loading}
                className={dark ? 'bg-gray-800' : ''}
            >
                <Empty description="No trend data available" />
            </Card>
        );
    }

    return (
        <Card 
            title={title} 
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
        >
            <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={formattedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#444' : '#eee'} />
                    <XAxis 
                        dataKey="date" 
                        stroke={dark ? '#888' : '#666'}
                        tick={{ fill: dark ? '#888' : '#666' }}
                    />
                    <YAxis 
                        yAxisId="left"
                        stroke={dark ? '#888' : '#666'}
                        tick={{ fill: dark ? '#888' : '#666' }}
                    />
                    <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        stroke={dark ? '#888' : '#666'}
                        tick={{ fill: dark ? '#888' : '#666' }}
                        domain={[0, 'auto']}
                    />
                    <Tooltip 
                        contentStyle={{ 
                            backgroundColor: dark ? '#1f1f1f' : '#fff',
                            border: `1px solid ${dark ? '#444' : '#ddd'}`,
                            color: dark ? '#fff' : '#000'
                        }}
                    />
                    <Legend />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="requests"
                        fill="#1890ff"
                        fillOpacity={0.3}
                        stroke="#1890ff"
                        name="Total Requests"
                    />
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="successes"
                        stroke="#52c41a"
                        strokeWidth={2}
                        dot={false}
                        name="Successful"
                    />
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="failures"
                        stroke="#ff4d4f"
                        strokeWidth={2}
                        dot={false}
                        name="Failed"
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="avgResponseTime"
                        stroke="#faad14"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        name="Avg Response (ms)"
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Card>
    );
};

export default ServiceMetricsTrendChart;
